import axiosInstance from "@/api/axios";
import { configToUse } from "@/Keycloak";
import { CancelToken } from "axios";
import {
  CreativeReportData,
  CreativeReportListItem,
  CreativeReportSection,
  CreativeReport,
} from "../creatives/types";
import { CreativeSectionConfiguration, CreativeReportMetadata, IAccount } from "@/types";
import {
  AccountBrand,
  BrandAd,
  FolderWithAds,
  GeneratedAdResponse,
  BrandConfigResponse,
  BrandCategory,
  CategoryBrandsResponse,
  BrandDetail,
  BulkAddBrandResponse,
} from "./types";
import { Folder } from "../creative-library/types";
import {
  CheckBrandUrlResponse,
  SearchBrandItem,
  CompetitorOverviewResponse,
  DefaultPromptsResponse,
  DefaultPrompt,
} from "./types";
import { DataConfigurations } from "@/types";
import { LocationCode } from "./components/competitor/filters/LocationDropdown";

const getAdFilters = (
  creativeType?: "video" | "image",
  adStatus?: "active" | "inactive",
) => {
  if (!creativeType && !adStatus) {
    return {};
  }

  return {
    filters: encodeURIComponent(
      JSON.stringify([
        ...(creativeType
          ? [
              {
                key: "creative.creative_type",
                conditions: [{ operator: "CONTAIN", value: creativeType }],
              },
            ]
          : []),
        ...(adStatus
          ? [
              {
                key: "status",
                conditions: [{ operator: "CONTAIN", value: adStatus }],
              },
            ]
          : []),
      ]),
    ),
  };
};

export const fetchCommentThemes = async (data: {
  accountId: string;
  ad_id?: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/analysis/comment/${data.accountId}/comment-themes/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.reportData && { ...data.reportData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      ...(data.ad_id && { ad_id: data.ad_id }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchCommentSentiments = async (data: {
  accountId: string;
  ad_id?: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/analysis/comment/${data.accountId}/comment-sentiments/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.reportData && { ...data.reportData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      ...(data.ad_id && { ad_id: data.ad_id }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchCommentMessagingAngles = async (data: {
  accountId: string;
  ad_id?: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/analysis/comment/${data.accountId}/comment-messaging-angles/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.reportData && { ...data.reportData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      ...(data.ad_id && { ad_id: data.ad_id }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchAllComments = async (data: {
  accountId: string;
  ad_id?: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/analysis/comment/${data.accountId}/all-comments/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.reportData && { ...data.reportData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      ...(data.ad_id && { ad_id: data.ad_id }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchAdList = async (data: {
  accountId: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/analysis/comment/${data.accountId}/ad-list/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.reportData && { ...data.reportData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchAccountBrands = async (data: {
  accountId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<AccountBrand[]>(
    `/analysis/competitors/${data.accountId}/brands/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const addAccountBrand = async (data: {
  accountId: string;
  brandId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<AccountBrand>(
    `/analysis/competitors/${data.accountId}/brands/`,
    {
      brand_id: data.brandId,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const removeAccountBrand = async (data: {
  accountId: string;
  brandId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.delete<{ message: string }>(
    `/analysis/competitors/${data.accountId}/brands/`,
    {
      data: { brand_id: data.brandId },
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchBrands = async (data: { cancelToken?: CancelToken }) => {
  const res = await axiosInstance.get<AccountBrand[]>(`/analysis/competitors/brands/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken: data.cancelToken,
  });
  return res.data;
};

export const fetchBrandAds = async (data: {
  brandId: string;
  sortType?: "running_days" | "newest" | "oldest";
  creativeType?: "video" | "image";
  adStatus?: "active" | "inactive";
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<{
    ads: BrandAd[];
    status: string;
    message: string;
  }>(`/analysis/competitors/brands/${data.brandId}/ads/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken: data.cancelToken,
    params: {
      sort_type: data.sortType,
      ...getAdFilters(data.creativeType, data.adStatus),
    },
  });
  return res.data;
};

export const fetchMultiBrandAds = async (data: {
  accountId: string;
  brandIds: string[];
  sortType?: "running_days" | "newest" | "oldest" | "impressions";
  creativeType?: "video" | "image";
  adStatus?: "active" | "inactive";
  pageSize?: number;
  cursor?: string | null;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  location?: LocationCode;
}) => {
  const filters = [];

  // Add creative type filter if specified
  if (data?.creativeType) {
    filters.push({
      key: "creative.creative_type",
      conditions: [{ operator: "=", value: data.creativeType }],
    });
  }

  // Add ad status filter if specified
  if (data?.adStatus) {
    filters.push({
      key: "status",
      conditions: [{ operator: "=", value: data.adStatus }],
    });
  }

  const res = await axiosInstance.post<{
    ads: BrandAd[];
    statuses: Record<string, string>;
    messages: Record<string, string>;
    config: {
      brand_ids: string[];
      page_size: number;
    };
    has_next: boolean;
    next_cursor: string | null;
    cursor: string | null;
    page_size: number;
    total_ad_count: number;
  }>(
    `/analysis/competitors/${data.accountId}/ads/`,
    {
      brand_ids: data.brandIds,
      sort_type: data.sortType || "",
      filters,
      ...(data.pageSize && { page_size: data.pageSize }),
      ...(data.cursor && { cursor: data.cursor }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data?.location && { location: data.location }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchBrandConfig = async (accountId: string) => {
  const res = await axiosInstance.get<BrandConfigResponse>(
    `/analysis/competitors/${accountId}/config/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchFolders = async (data: {
  accountId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<Folder[]>(
    `/analysis/competitors/${data.accountId}/folders/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const createFolder = async (data: {
  accountId: string;
  name: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<Folder>(
    `/analysis/competitors/${data.accountId}/folders/`,
    { name: data.name },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchFolderAds = async (data: {
  accountId: string;
  folderId: string;
  sortType?: "running_days" | "newest" | "oldest";
  creativeType?: "video" | "image";
  adStatus?: "active" | "inactive";
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<FolderWithAds>(
    `/analysis/competitors/${data.accountId}/folders/${data.folderId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
      params: {
        sort_type: data.sortType,
        ...getAdFilters(data.creativeType, data.adStatus),
      },
    },
  );
  return res.data.ads;
};

export const saveAdToFolder = async (data: {
  accountId: string;
  folderId: string;
  adId: string;
  name: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.put<{ id: string; name: string }>(
    `/analysis/competitors/${data.accountId}/folders/${data.folderId}/`,
    {
      ad_id: data.adId,
      name: data.name,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const removeAdFromFolder = async (data: {
  accountId: string;
  folderId: string;
  adId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.delete<{
    message: string;
  }>(`/analysis/competitors/${data.accountId}/folders/${data.folderId}/ads/`, {
    data: {
      ad_id: data.adId,
    },
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken: data.cancelToken,
  });
  return res.data;
};

export const fetchAccountBrandAds = async (data: {
  accountId: string;
  brandId: string;
  sortType?: "running_days" | "newest" | "oldest";
  creativeType?: "video" | "image";
  adStatus?: "active" | "inactive";
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<{
    ads: BrandAd[];
    status: string;
    message: string;
    brand: AccountBrand;
  }>(`/analysis/competitors/${data.accountId}/brands/${data.brandId}/ads/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken: data.cancelToken,
    params: {
      sort_type: data.sortType,
      ...getAdFilters(data.creativeType, data.adStatus),
    },
  });
  return res.data;
};

export interface GenerateAdParams {
  accountId: string;
  adIds: string[];
  defaultPromptId: string | null;
  prompt?: string | null;
  fileIds?: string[];
  libraryFileIds?: string[];
  aspectRatio?: string | null;
  numberOfMedia?: number;
  model?: string;
  avoidDangerZone?: boolean;
  moodboardId?: string | null;
  cancelToken?: CancelToken;
}

export const generateAd = async (data: GenerateAdParams) => {
  const res = await axiosInstance.post<GeneratedAdResponse>(
    `/analysis/competitors/${data.accountId}/generate-ad/`,
    {
      ad_ids: data.adIds,
      default_prompt_id: data.defaultPromptId,
      ...(data.prompt !== undefined && { prompt: data.prompt }),
      file_ids: data.fileIds ?? [],
      library_file_ids: data.libraryFileIds ?? [],
      aspect_ratio: data.aspectRatio === "Auto" ? null : (data.aspectRatio ?? "1:1"),
      number_of_media: data.numberOfMedia ?? 4,
      model: data.model ?? "gpt",
      avoid_danger_zone: data.avoidDangerZone ?? false,
      ...(data.moodboardId ? { moodboard_id: data.moodboardId } : {}),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchDefaultPrompts = async (data: {
  accountId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<DefaultPromptsResponse>(
    `/analysis/competitors/${data.accountId}/default-prompts/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const createDefaultPrompt = async (data: {
  accountId: string;
  prompt: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<{ status: string; data: DefaultPrompt }>(
    `/analysis/competitors/${data.accountId}/default-prompts/`,
    { prompt: data.prompt },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const updateDefaultPrompt = async (data: {
  accountId: string;
  promptId: string;
  prompt: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.put<{ status: string; data: DefaultPrompt }>(
    `/analysis/competitors/${data.accountId}/default-prompts/`,
    { id: data.promptId, prompt: data.prompt },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const deleteDefaultPrompt = async (data: {
  accountId: string;
  promptId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.delete<{ status: string; message: string }>(
    `/analysis/competitors/${data.accountId}/default-prompts/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
      data: {
        id: data.promptId,
      },
    },
  );
  return res.data;
};

export const renameFolder = async (data: {
  accountId: string;
  folderId: string;
  name: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.put<Folder>(
    `/analysis/competitors/${data.accountId}/folders/${data.folderId}/`,
    { name: data.name },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const deleteFolder = async (data: {
  accountId: string;
  folderId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.delete<Folder>(
    `/analysis/competitors/${data.accountId}/folders/${data.folderId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const checkBrandUrl = async (data: {
  pageUrl: string;
  brandPlatform: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<CheckBrandUrlResponse>(
    `/analysis/competitors/check-brand-url/`,
    {
      page_url: data.pageUrl,
      brand_platform: data.brandPlatform,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const addBrand = async (data: {
  accountId: string;
  brandDetails: BrandDetail[];
  addPrivateBrands?: boolean;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<BulkAddBrandResponse>(
    `/analysis/competitors/brands/bulk/`,
    {
      account_id: data.accountId,
      brand_details: data.brandDetails,
      add_private_brand: data.addPrivateBrands ?? false,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const getBrandStatus = async (data: {
  brandId: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<{
    status: string;
    message: string;
    brand: AccountBrand;
  }>(`/analysis/competitors/brand-status/${data.brandId}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken: data.cancelToken,
  });
  return res.data;
};

export const searchBrands = async (data: {
  query: string;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.get<{ data: SearchBrandItem[] }>(
    `/analysis/competitors/search-brand/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
      params: {
        query: data.query,
      },
    },
  );
  return res.data;
};

export const fetchCompetitorReportList = async (accountId: string) => {
  const res = await axiosInstance.get<{
    status: string;
    reports: CreativeReportListItem[];
  }>(`/creative/${accountId}/report/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      report_type: "competitor",
    },
  });
  return res.data;
};

export const fetchCompetitorReportById = async (
  accountId: string,
  reportId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<CreativeReport>(
    `/creative/${accountId}/report/${reportId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const updateCompetitorReport = async (
  accountId: string,
  reportId: string,
  data: Partial<CreativeReport>,
) => {
  const res = await axiosInstance.put<{ status: string; report: CreativeReport }>(
    `/creative/${accountId}/report/${reportId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const duplicateCompetitorReport = async (accountId: string, reportId: string) => {
  const res = await axiosInstance.post<CreativeReportListItem>(
    `/creative/${accountId}/report/${reportId}/duplicate/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const updateCompetitorReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
  data: {
    title?: string;
    icon?: string;
    configuration?: CreativeSectionConfiguration;
  },
) => {
  const res = await axiosInstance.put<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const duplicateCompetitorReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
) => {
  const res = await axiosInstance.post<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/duplicate/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const deleteCompetitorReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const updateSectionOrders = async (
  accountId: string,
  reportId: string,
  sectionOrders: Array<{
    section_id: string;
    order: number;
  }>,
) => {
  const res = await axiosInstance.put<{
    status: string;
    sections: CreativeReportSection[];
  }>(
    `/creative/${accountId}/report/${reportId}/sections/`,
    {
      section_orders: sectionOrders,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const addSectionToReport = async (
  accountId: string,
  reportId: string,
  data: {
    title: string;
    icon?: string;
    configuration?: {
      dropdowns: {
        group_by: string | null;
        competitors: string[];
        creative_type?: string | null;
      };
    };
    description?: string | null;
  },
) => {
  const res = await axiosInstance.post<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const fetchCompetitorReportDataConfigurations = async (
  accountId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<{
    status: string;
    configuration: DataConfigurations;
  }>(`/creative/${accountId}/data-configurations/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken,
    params: {
      report_type: "competitor",
    },
  });
  return res.data;
};

export const deleteCompetitorReport = async (accountId: string, reportId: string) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative/${accountId}/report/${reportId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        report_type: "competitor",
      },
    },
  );
  return res.data;
};

export const createCompetitorReport = async (
  accountId: string,
  data: Partial<CreativeReportListItem> & {
    section_configuration: Partial<CreativeSectionConfiguration>;
    report_type: string;
  },
) => {
  const res = await axiosInstance.post<{
    status: string;
    report: CreativeReport;
  }>(`/creative/${accountId}/report/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      report_type: "competitor",
    },
  });
  return res.data;
};

export const fetchOrUpdateCompetitorSectionData = async (data: {
  accountId: string;
  sectionId: string;
  reportMetadata?: CreativeReportMetadata;
  sectionConfiguration?: CreativeSectionConfiguration;
  creative_type?: string;
  cancelToken?: CancelToken;
  isPolling?: boolean;
  operation_mode?: "update_only" | "fetch_only" | "both";
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/creative/${data.accountId}/data/${data.sectionId}/`,
    {
      ...(data.sectionConfiguration && { ...data.sectionConfiguration }),
      ...(data.reportMetadata && { ...data.reportMetadata }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      report_type: "competitor",
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchCompetitorOverview = async (data: {
  accountId: string;
  date: {
    label: string;
    start_date: string;
    end_date: string;
  };
  brand_ids: string[];
  filters?: Array<{
    key: string;
    conditions: Array<{
      operator: string;
      value: string;
    }>;
  }>;
  location?: string | null;
  cancelToken?: CancelToken;
}): Promise<CompetitorOverviewResponse> => {
  const res = await axiosInstance.post(
    `/analysis/competitors/${data.accountId}/ads/overview/`,
    {
      date: data.date,
      brand_ids: data.brand_ids,
      filters: data.filters || [],
      location: data.location || null,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data as CompetitorOverviewResponse;
};

export const addDemoAccount = async (data: {
  brandId: string;
  cancelToken?: CancelToken;
}): Promise<IAccount> => {
  const res = await axiosInstance.post<IAccount>(
    `accounts/create-demo-account/`,
    { brand_id: data.brandId },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL, cancelToken: data.cancelToken },
  );
  return res.data;
};

export const fetchBrandCategories = async (data: { cancelToken?: CancelToken }) => {
  const res = await axiosInstance.get<BrandCategory[]>(
    `/analysis/competitors/brand-category-list/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};

export const fetchCategoryBrands = async (data: {
  categoryIds: string[];
  pageNum?: number;
  pageSize?: number;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<CategoryBrandsResponse>(
    `/analysis/competitors/brand-category-search/`,
    {
      category_ids: data.categoryIds,
      ...(data.pageNum && { page_num: data.pageNum }),
      ...(data.pageSize && { page_size: data.pageSize }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
    },
  );
  return res.data;
};
